/**
 * voiceFidelitySlice
 * ===========================================================================
 * Phase 4D — VA Fidelity Gate (P2 client integration).
 *
 * Holds the per-sample fidelity-scoring state returned by
 * `POST /api/voice-snippets/score`. State is keyed by `sampleUuid` so the
 * RecordingStudio can render the right band for the right table row without
 * any prop drilling.
 *
 * Shape:
 *   state[sampleUuid] = {
 *     status:   'idle' | 'scoring' | 'scored' | 'error',
 *     envelope: FidelityEnvelope | null,    // see server FidelityScoringService
 *     error:    string | null,
 *   }
 *
 * The server contract (design §6.1) is that scoring failures — timeouts,
 * provider errors, network problems — produce a 200 with a GRAY-band
 * envelope, never a non-200. So on the happy path this thunk's `fulfilled`
 * action always carries a valid envelope.
 *
 * Defense-in-depth: if the request itself fails (network down, 4xx, 5xx, or
 * our own client-side timeout fires — CLIENT_TIMEOUT_MS, set above the
 * server's ASR budget; see its comment), we
 * **synthesize** a GRAY envelope on the client and return it via `fulfilled`

 * anyway. The UI treats GRAY identically — band glyph, no diff, Save unlocked
 * — so the VA is never blocked from saving by a broken scoring path.
 *
 * Why not `rejected`? Because reducer-land "error" would force the UI to
 * branch on `status === 'error'` everywhere. By collapsing all failure
 * modes into GRAY at the thunk boundary, the panel component stays
 * branch-free: it only ever looks at `envelope.fidelityBand`.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';
import { config } from '../constants/config';

// Client-side hard timeout. MUST be strictly GREATER than the server's own ASR
// orchestrator timeout (voiceFidelityThresholds.json → timeoutMs, currently
// 20 s) plus upload/network headroom — otherwise the client axios abort wins
// the race and the VA sees a client-synthesized GRAY *before* the server can
// return its real verdict (or its own GRAY). That race is exactly what bit us
// in early prod testing: client 8 s == server 8 s, so a ~12 s read that Whisper
// needed >8 s for aborted client-side ('timeout of 8000ms exceeded',
// status: undefined) and never got scored. Keep this comfortably above the
// server timeout: 25 s = 20 s server ASR budget + ~5 s upload/round-trip slack.
const CLIENT_TIMEOUT_MS = 25000;


/**
 * Build a synthetic GRAY-band envelope. Used when the network/HTTP layer
 * fails before the server's orchestrator gets a chance to construct one.
 */
function synthesizeGrayEnvelope({ sampleUuid, errorReason }) {
  return {
    sampleUuid: sampleUuid || null,
    fidelityScore: null,
    fidelityBand: 'GRAY',
    fidelityComputedAt: new Date().toISOString(),
    fidelityProviderId: null,
    fidelityBreakdown: null,
    transcript: null,
    transcriptDiff: null,
    fidelityLatencyMs: null,
    errorReason: errorReason || 'Scoring service unreachable.',
  };
}

/**
 * scoreVoiceSnippet — POST /api/voice-snippets/score
 *
 * @param {object}   payload
 * @param {Blob}     payload.audioBlob    — the WebM/Opus blob from MediaRecorder
 * @param {string}   payload.promptText   — exact text the VA was asked to read
 *                                          (use editedText[uuid] if present)
 * @param {string}   payload.sampleUuid   — CharSpeechSample uuid (for keying)
 * @param {string}   [payload.mimeType]   — defaults to 'audio/webm'
 *
 * Resolves with the server's FidelityEnvelope, or a synthesized GRAY
 * envelope on any failure. Never rejects (under normal use).
 */
export const scoreVoiceSnippet = createAsyncThunk(
  'voiceFidelity/scoreVoiceSnippet',
  async ({ audioBlob, promptText, sampleUuid, mimeType }, { rejectWithValue }) => {
    if (!audioBlob) {
      // Programmer error — bail loud. The reducer handles `rejected` by
      // setting status='error' so the dev sees it during integration.
      return rejectWithValue({ message: 'audioBlob is required' });
    }
    if (typeof promptText !== 'string' || promptText.trim().length === 0) {
      return rejectWithValue({ message: 'promptText is required' });
    }

    const formData = new FormData();
    // The filename is mostly cosmetic but Whisper does sniff the extension
    // as a content-type fallback, so we keep it honest about what we're
    // uploading (see RecordingStudio's voice-file naming).
    const ext = (mimeType || audioBlob.type || 'audio/webm').includes('wav') ? 'wav' : 'webm';
    formData.append('audio', audioBlob, `snippet.${ext}`);
    formData.append('promptText', promptText);
    if (sampleUuid) formData.append('sampleUuid', sampleUuid);

    try {
      const response = await apiClient.upload(
        config.SCORE_VOICE_SNIPPET,
        formData,
        {},
        { timeout: CLIENT_TIMEOUT_MS }
      );
      // Server always 200s under the design contract — even on its own
      // timeout it returns a GRAY envelope. So response.data IS the envelope.
      return response.data;
    } catch (err) {
      // Network failure, 4xx, 5xx, axios client timeout, CORS — collapse
      // them all into a GRAY envelope. Log enough for debugging without
      // echoing anything sensitive (the server already scrubs).
      const status = err?.response?.status;
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      const reason = isTimeout
        ? `Scoring timed out after ${CLIENT_TIMEOUT_MS / 1000} seconds.`
        : status
          ? `Scoring service returned HTTP ${status}.`
          : 'Scoring service unreachable.';
      // eslint-disable-next-line no-console
      console.warn('[voiceFidelitySlice] score failed:', { status, isTimeout, message: err?.message });
      return synthesizeGrayEnvelope({ sampleUuid, errorReason: reason });
    }
  }
);

const voiceFidelitySlice = createSlice({
  name: 'voiceFidelity',
  initialState: {},
  reducers: {
    /**
     * Reset state for a single sample. Fired when the VA re-records — we
     * want the panel to disappear (return to no-score) until the new take
     * completes.
     */
    clearFidelityScore: (state, action) => {
      const { sampleUuid } = action.payload || {};
      if (sampleUuid) {
        delete state[sampleUuid];
      }
    },
    /**
     * Wipe everything. Used on RecordingStudio unmount to keep memory tidy.
     */
    clearAllFidelityScores: () => ({}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(scoreVoiceSnippet.pending, (state, action) => {
        const { sampleUuid } = action.meta.arg || {};
        if (!sampleUuid) return;
        state[sampleUuid] = {
          status: 'scoring',
          envelope: null,
          error: null,
        };
      })
      .addCase(scoreVoiceSnippet.fulfilled, (state, action) => {
        const { sampleUuid } = action.meta.arg || {};
        if (!sampleUuid) return;
        state[sampleUuid] = {
          status: 'scored',
          envelope: action.payload,   // may be a real envelope OR synthesized GRAY
          error: null,
        };
      })
      .addCase(scoreVoiceSnippet.rejected, (state, action) => {
        // Reached only on programmer-error rejects above (missing audio /
        // prompt). Real network failures resolve to GRAY in `fulfilled`.
        const { sampleUuid } = action.meta.arg || {};
        if (!sampleUuid) return;
        state[sampleUuid] = {
          status: 'error',
          envelope: null,
          error: action.payload?.message || 'Unknown scoring error.',
        };
      });
  },
});

export const { clearFidelityScore, clearAllFidelityScores } = voiceFidelitySlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────

/**
 * Select the fidelity state for a given sample. Returns `null` (not
 * `undefined`) when nothing has been scored yet, so consumer components
 * can do `if (!state) return null` cleanly.
 */
export const selectFidelityForSample = (state, sampleUuid) =>
  state.voiceFidelity?.[sampleUuid] || null;

export default voiceFidelitySlice.reducer;
